// Auth endpoints. One universal login resolves admin or student server-side
// (controllers/logIn.js), which is exactly what the UI's single login form
// expects — there is no client-side role guessing.

import { post, get, setToken } from './client';
import { toUiUser } from './adapters';

/**
 * POST /login — the only endpoint whose token sits OUTSIDE `data`, so it reads
 * the raw payload. Stores the token before returning so the very next call is
 * already authenticated.
 */
export async function login(email, password) {
  const payload = await post('/login', { email: String(email).trim(), password }, { raw: true });
  const token = payload?.token;
  if (!token) throw new Error('Login succeeded but no token was returned.');
  setToken(token);
  return toUiUser(payload?.data);
}

// GET /login/me — rehydrates a session from a stored token on boot. Does not
// echo `email`, so pass anything already known to fill the gap.
export async function me(known = {}) {
  const data = await get('/login/me');
  return toUiUser(data, known);
}

export function logout() {
  setToken(null);
}

/**
 * GET /login/getAllGroups — public, and double-nested: the array is at
 * `data.data`. Returns SelectField-ready options.
 */
export async function getGroups() {
  const data = await get('/login/getAllGroups');
  const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  return rows.map((g) => ({ value: g.groupName, label: g.groupName, id: g.groupId }));
}

// POST /student/studentRegister. The account is created unverified — an admin
// must approve it before the student can log in (models/student_model.js sets
// verified:false, and login rejects with 403 until then).
export async function registerStudent(form) {
  return post('/student/studentRegister', {
    studentName: form.studentName,
    studentEmail: form.studentEmail,
    password: form.password,
    studentPhoneNumber: form.studentPhoneNumber,
    parentPhoneNumber: form.parentPhoneNumber,
    parentEmail: form.parentEmail,
    birthDate: form.birthDate,
    group: form.group,
    semester: form.semester,
  });
}

// --- password reset (three steps, one modal) ---------------------------------

export const requestPasswordReset = (email) =>
  post('/login/forgetPassword', { email: String(email).trim() });

export const verifyOtp = (email, otp) =>
  post('/login/otp', { email: String(email).trim(), otp: String(otp).trim() });

export const resetPassword = (email, password) =>
  post(`/login/resetPassword/${encodeURIComponent(String(email).trim())}`, { password });
